import { useRef, useState } from 'react';

import InfiniteScroll from 'react-infinite-scroll-component';
import get from 'ts-get';

import { useRutilusQuery } from '../../../../../../api/graphql/useRutilusQuery';
import { SaveButton } from '../../../../../../components/Clickables/Buttons/SaveButton';
import { Heading } from '../../../../../../components/Heading/Heading';
import { LoadingSpinner } from '../../../../../../components/LoadingSpinner/LoadingSpinner';
import { PostsViewer } from '../../../../../../components/PostsViewer/PostsViewer';
import { useAnalytics } from '../../../../../../contexts/AnalyticsContext/AnalyticsContext';
import { trackEvent } from '../../../../../../helpers/analytics/trackEvent';
import { reportMykissClientError } from '../../../../../../helpers/errorHandling/client';
import { getFishingForbidden } from '../../../../../../helpers/FishingWater/getFishingForbidden';
import { getWaterPrivacyStatusFromMetadata } from '../../../../../../helpers/FishingWater/getWaterPrivacyStatusFromMetadata';
import { getNodesFromConnection } from '../../../../../../helpers/graphql';
import { useClientHasRendered } from '../../../../../../helpers/hooks';
import { usePostViewer } from '../../../../../../helpers/hooks/usePostViewer';
import { useUserInfo } from '../../../../../../helpers/hooks/useUserInfo';
import {
  GetFishingWaterSummary,
  GetFishingWaterSummaryVariables,
  GetFishingWaterSummary_fishingWater_catches_edges_node as IFWCatch,
} from '../../../../../../interfaces/graphql';
import { getFishingWaterSummary } from '../../../../../../queries/rutilus/FishingWaters';
import { CATCHES_PER_REQUEST, CATCHES_PER_REQUEST_FREE } from '../../../../constants/constants';
import { MapDrawerLayout } from '../../MapDrawerLayout';
import { MapDrawerNotification } from '../../MapDrawerNotification/MapDrawerNotification';
import { MapDrawerSignup } from '../../MapDrawerSignup/MapDrawerSignup';

import { FishingWaterViewCatchesList } from './FishingWaterViewCatchesList';
import { FishingWaterViewHeader } from './FishingWaterViewHeader';

interface IProps {
  waterId: string;
}

export const FishingWaterView = ({ waterId }: IProps) => {
  const { currentPage } = useAnalytics();
  const clientHasRendered = useClientHasRendered();
  const { isLoggedIn } = useUserInfo();
  const { loading, data, fetchMore } = useRutilusQuery<
    GetFishingWaterSummary,
    GetFishingWaterSummaryVariables
  >(getFishingWaterSummary, {
    variables: {
      externalId: waterId,
      first: isLoggedIn ? CATCHES_PER_REQUEST : CATCHES_PER_REQUEST_FREE,
      after: '',
    },
  });
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const postsReference = useRef<HTMLDivElement>(null);
  const {
    currentPostViewerId,
    isPostViewerOpen,
    closePostViewer,
    openPostViewer,
    setCurrentPostViewerId,
  } = usePostViewer(
    postsReference,
    // eslint-disable-next-line no-magic-numbers
    8,
    false,
  );

  if ((clientHasRendered && loading) || !clientHasRendered) {
    return (
      <MapDrawerLayout className="col-start-1 row-start-1 h-full">
        <div className="flex h-full flex-col">
          <LoadingSpinner loadingText="Loading water..." />
        </div>
      </MapDrawerLayout>
    );
  }

  if (!data) {
    return (
      <MapDrawerLayout className="col-start-1 row-start-1 h-full">
        <div className="flex h-full flex-col">
          <div className="flex">
            <Heading variant="sm" className="pr-2">
              Not Found
            </Heading>
          </div>
          <MapDrawerNotification title="It looks like something went wrong" />
        </div>
      </MapDrawerLayout>
    );
  }

  const hasNextPage = get(data, d => d.fishingWater.catches.pageInfo.hasNextPage, false);
  const endCursor = get(data, d => d.fishingWater.catches.pageInfo.endCursor);
  const canLoadMore = !loading && hasNextPage && !isFetchingMore;
  const region = get(data, d => d.fishingWater.region.name);
  const country = get(data, d => d.fishingWater.country.id);
  const totalAverageRating = get(data, d => d.fishingWater.allReviews.totalAverage, 0);
  const totalRatingCount = get(data, d => d.fishingWater.allReviews.totalCount, 0);

  const {
    displayName,
    latitude,
    longitude,
    catches,
    catchesCount,
    _id: externalId,
    metadata,
  } = data.fishingWater;

  const fetchNextPage = async (): Promise<unknown> => {
    if (!isLoggedIn) {
      return;
    }
    setIsFetchingMore(true);

    void trackEvent({
      name: 'mykiss_load_more',
      properties: {
        page: currentPage,
        manual: true,
        source: 'explore_water_catches',
      },
    });

    return fetchMore({
      context: {},
      variables: {
        after: endCursor,
        first: CATCHES_PER_REQUEST,
      },
      updateQuery: (prev: GetFishingWaterSummary, { fetchMoreResult }): GetFishingWaterSummary => {
        if (!fetchMoreResult.fishingWater.catches) {
          return prev;
        }
        return {
          fishingWater: {
            ...fetchMoreResult.fishingWater,
            catches: {
              ...fetchMoreResult.fishingWater.catches,
              edges: [
                ...get(prev, p => p.fishingWater.catches.edges, []),
                ...get(fetchMoreResult, r => r.fishingWater.catches.edges, []),
              ],
              pageInfo: {
                ...get(
                  fetchMoreResult,
                  r => r.fishingWater.catches.pageInfo,
                  prev.fishingWater.catches.pageInfo,
                ),
              },
            },
          },
        };
      },
    })
      .then(() => {
        setIsFetchingMore(false);
      })
      .catch(reportMykissClientError);
  };

  const catchPostList = getNodesFromConnection<IFWCatch>(catches).map(_catch => _catch.post);
  const waterPrivacyStatus = getWaterPrivacyStatusFromMetadata(metadata);
  const fishingForbidden = getFishingForbidden(waterPrivacyStatus);

  return (
    <MapDrawerLayout
      drawerRef={postsReference}
      contentId="waters-list"
      headerContent={
        <FishingWaterViewHeader
          displayName={displayName}
          region={region}
          country={country}
          totalAverageRating={totalAverageRating}
          totalRatingCount={totalRatingCount}
          catchesCount={catchesCount}
          longitude={longitude}
          latitude={latitude}
          externalId={externalId}
          metadata={metadata}
          fishingForbidden={fishingForbidden}
        />
      }
    >
      {!isLoggedIn && (
        <MapDrawerSignup
          headingText="See all catches in the Fishbrain app"
          bodyText="In the app you can see all catches in this area"
          downloadButtonText="Download the app"
        />
      )}
      <InfiniteScroll
        next={fetchNextPage}
        dataLength={catchPostList.length}
        hasMore={canLoadMore}
        scrollableTarget="waters-list"
        loader={<></>}
      >
        <div className="flex h-full flex-col">
          {!fishingForbidden && catchesCount > 0 && (
            <>
              <div>
                <FishingWaterViewCatchesList
                  catchPosts={catchPostList}
                  openPostViewer={openPostViewer}
                />
              </div>

              {hasNextPage && isLoggedIn && (
                <div className="my-8 flex w-full justify-center">
                  <SaveButton
                    onClick={() => {
                      void fetchNextPage();
                    }}
                    variant="buttonPrimary"
                    savingLabel="Loading..."
                    isSaving={loading}
                  >
                    Load more
                  </SaveButton>
                </div>
              )}
            </>
          )}

          {!fishingForbidden && catchesCount === 0 && (
            <MapDrawerNotification title="No catches in this water" withIcon />
          )}

          {fishingForbidden && <MapDrawerNotification title="Fishing is forbidden here" withIcon />}
        </div>
      </InfiniteScroll>
      {isPostViewerOpen && currentPostViewerId && (
        <PostsViewer
          posts={catchPostList}
          currentPostViewerId={currentPostViewerId}
          hasMore={canLoadMore}
          isPostViewerOpen
          loadMore={() => {
            void fetchNextPage();
          }}
          onClosePostsViewer={closePostViewer}
          setCurrentPostViewerId={setCurrentPostViewerId}
        />
      )}
    </MapDrawerLayout>
  );
};
